import React, { useEffect } from "react";
import {
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Typography,
} from "@mui/material";
import { Field } from "../../../../Interface/scheduler/form";
import { useFormContext } from "../../../contexts/FormContext";
import "./CleaningRadioSection.css";
import FormBuilder from "../../FormBuilder/FormBuilder";
import { useErrorClassClassifier } from "../../../hooks/useErrorClassClassifier";
import { sessionStorageUtils } from "../../../../utility/utility";

interface CleaningRadioSectionProps {
  field: Field;
  error?: string | null;
  handleFunction: (e: React.ChangeEvent<HTMLInputElement>) => void;
  errors: { [key: string]: string };
  setErrors: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>;
}

const CleaningRadioSection: React.FC<CleaningRadioSectionProps> = ({
  field: {
    id: fieldId,
    label: fieldLabel,
    required: fieldRequired,
    options: fieldOptions,
    defaultSelected,
  },
  error,
  handleFunction,
  errors,
  setErrors,
}) => {
  const { formValues, setFormValues } = useFormContext();

  useEffect(() => {
    const serviceTypeFromSession = sessionStorageUtils.getItem("serviceType");
    if (defaultSelected && fieldOptions?.length > 0 && !formValues[fieldId]) {
      const defaultOption =
        serviceTypeFromSession === "commercial"
          ? fieldOptions?.find(
              (option) => option.id?.toLowerCase() === serviceTypeFromSession,
            )
          : fieldOptions[0];
      setFormValues((prevValues) => ({
        ...prevValues,
        isMlyFlow: true,
        [fieldId]: defaultOption.id,
        ...(fieldId === "cleaning-service" && {
          servicetype: defaultOption.id,
        }),
      }));
    }
  }, [defaultSelected, fieldOptions, fieldId]);

  const selectedServiceOption = fieldOptions?.find(
    (option) => option.id === formValues[fieldId],
  );

  return (
    <>
      <FormControl
        component="div"
        data-testid={fieldId}
        className={fieldId}
        fullWidth
      >
        <div className={`cleaning-label ${fieldId}`}>
          {fieldLabel}
          {fieldRequired && <sup>*</sup>}
        </div>
        <RadioGroup
          name={fieldId}
          value={formValues[fieldId] || ""}
          onChange={handleFunction}
          className={useErrorClassClassifier({
            error: !!error?.length,
            errorClassToApply: "checkbox-container-highlight input-label-wrap",
            defaultClass: "input-label-wrap",
          })}
          row
          data-testid={"radio-group-" + fieldId}
        >
          {fieldOptions?.map(({ id, label }) => (
            <FormControlLabel
              key={id}
              value={id}
              control={<Radio />}
              data-testid={id}
              label={
                <Typography id={fieldId + "-text"} style={{ fontSize: "1rem" }}>
                  {label}
                </Typography>
              }
            />
          ))}
        </RadioGroup>
        {error && (
          <div className="error-message" role="alert" aria-live="polite">
            {error}
          </div>
        )}
      </FormControl>
      <div className={selectedServiceOption?.id || ""}>
        {selectedServiceOption?.child?.map((serviceDetail: Field) => (
          <div
            className={serviceDetail.id}
            data-testid={serviceDetail.id}
            key={serviceDetail.id}
          >
            <FormBuilder
              field={serviceDetail}
              errors={errors}
              setErrors={setErrors}
            />
          </div>
        ))}
      </div>
    </>
  );
};

export default CleaningRadioSection;
